import React, {useRef, useState, useEffect, useContext, useCallback} from 'react'

import Card from './Card';
import { Event } from '../types';
import i18n from 'live/services/i18n';
import styled from 'styled-components';
import { DashboardContext } from 'live/context/dashboard';
import { useVirtualList} from 'ahooks';
import { useScroll } from '../hooks/useScroll';
import { Geocoding, LatLng } from '../classes/Geocoding';


type Props = {
    cardHeight: number, // IMPORTANT: This value define height from virtualListCard and without it we can have throuble in scroll down with big empty space
    values: Event[],
    qualifier: string,
    loading: boolean
}

const ListCards = ({cardHeight = 100, values, qualifier}: Props): JSX.Element => {
   
    const appendInLookup = (lookup = {}) => {
        const auxLookups = context?.params?.filter ? {...JSON.parse(context.params.filter)} : {}
        
        Object.keys(lookup).forEach(key => {
            auxLookups[key] = lookup[key]
        })
        
        context.actions.setFilterValues(auxLookups);
    }

    const clearLookup = (lookup = {}) => {
        const auxLookups = context?.params?.filter ? {...JSON.parse(context.params.filter)} : {}
        
        Object.keys(lookup).forEach(key => {
            delete auxLookups[key]
        })
        
        context.actions.setFilterValues(auxLookups);
    }

    const onClickCard = (id, lookup) => {
        if(id === cardSelected){                
            setCardSelected(undefined)            
            clearLookup(lookup)
        } 
        else{            
            setCardSelected(id)            
            appendInLookup(lookup)
        }
    }

    const fillAddress = async (list, geocoding: Geocoding) => {      
        const latLngs: LatLng[] = [];
        
        list?.forEach( ({data}) => {
            const {locationBegin, locationEnd} = data

            if(locationBegin) latLngs.push(locationBegin)

            if(locationEnd) latLngs.push(locationEnd)
        })
        
        await geocoding?.fillAddresses(latLngs) 

        if(geocoding){
            setAddresses(curr => {            
                return {...curr, ...geocoding.getAddresses()}
            })
        }
    }

    const fillWithDiffListAddress = (list, geocoding: Geocoding) => {        
        const diff = list.filter(ele => !cardPrevListRef.current.some(ele2 => ele2.data.id === ele.data.id ))        
        fillAddress(diff, geocoding)
        const aux = diff.filter(ele => !cardPrevListRef.current.some(ele2 => ele2.data.id === ele.data.id))
        cardPrevListRef.current = [...cardPrevListRef.current, ...aux]
    }

    const EmptyMessage = ({show}): JSX.Element => {

        if(!show) return <></>

        return <WrapperEmptyData>
                <div>
                    <i className="far fa-route"></i>
                    <span style={{fontWeight: 800}}>{i18n('No trips found')}</span>
                    <span>  {i18n('for the selected timespan')}</span>
                    <span>{i18n('Select a new timespan or apply new filters')}.</span>
                </div>
            </WrapperEmptyData>

    }

    const handleScrollTo = (values, cardSelected) => {        
        const index = values?.findIndex(ele => ele.id == cardSelected)                        
        if(index != -1) scrollTo(index)
        else scrollTo(0)
    }

    const containerRef = useRef(null);
    const wrapperRef = useRef(null);
    const context: any = useContext(DashboardContext);

    const [cardSelected, setCardSelected] = useState<any>()  
    const [loading, setLoading] = useState<boolean>(true)    
    const [geocodingWasLoad, setGeocodingWasLoad] = useState<boolean>()                
    const [prevValues, setPrevValues] = useState<Event[]>(values)  
    
    
    const geocodingRef = useRef<Geocoding>()   
    
   
    const [list, scrollTo] = useVirtualList(values, {
        containerTarget: containerRef,
        wrapperTarget: wrapperRef,
        itemHeight: cardHeight, 
        overscan: 5,
    });

    const itemsRef = useRef<HTMLDivElement>();    
    const cardPrevListRef = useRef<{data: any, index: number}[]>([]);    
    const [addresses, setAddresses] = useState<any>({})

    const {isScrolling} = useScroll(containerRef.current, 800)
    let loadingTimeout = null

    useEffect(() => {
        return () => {
            clearTimeout(loadingTimeout)
        }
    }, [])

    useEffect(() => {   
        if(context?.params?.filter){
            const cardSelectedID = JSON.parse(context.params.filter)?.drilldown_id                      
            setCardSelected(cardSelectedID)            
        } 
    }, [context?.params?.filter])

    useEffect(() => {
        if(geocodingRef.current){
            setLoading(true)
            clearTimeout(loadingTimeout)
    
            loadingTimeout = setTimeout(() => { 
                setLoading(false)                
            }, 1000)
        }
    }, [geocodingRef.current, values])
    
    useEffect(() => {
        if(geocodingRef.current && loading === false && isScrolling === false){
            if(values !== prevValues) handleScrollTo(values, cardSelected)
            fillWithDiffListAddress(list, geocodingRef.current)
            setPrevValues(values)
        }
    }, [loading, geocodingRef.current, isScrolling])
   

    useEffect(() => {
        if(!geocodingWasLoad){                          
            geocodingRef.current = new Geocoding(qualifier)  
            setGeocodingWasLoad(true)
        }
    }, [geocodingWasLoad])

    return <>
        <ContainerCards ref={containerRef}>                
            <div ref={wrapperRef}>

                {loading && <WrapperLoading><div className="loading" /></WrapperLoading>}

                {
                    !loading && 
                    <>
                        <EmptyMessage show={list?.length === 0}/>
               
                        <div ref={itemsRef} style={{padding: "0px 5px 0 10px"}}>
                            {
                                list?.map(({data}) => {
                                    const {id, lookup} = data
                
                                    return <Card key={id} data={data} onClick={() => onClickCard(id, lookup)} addresses={addresses}
                                            isActive={lookup.drilldown_id?.toString().trim() === cardSelected?.toString().trim()} />
                                        
                                })
                            }
                        </div> 
                    </>
                }
            </div>
            
        </ContainerCards>
    </> 
}

export default React.memo(ListCards);

const ContainerCards = styled.div`
    height: 69.5vh;  
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }

    &::-webkit-scrollbar-track {
        border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
        background: #bac0cb;
        border-radius: 10px;
    }
`

const WrapperLoading = styled.div`    
    height: 50px;
`

const WrapperEmptyData = styled.div`
    display: flex;
    height: 69.5vh; 
    text-align: center;
    justify-content: center;
    align-items: center;
    flex-direction: column;   
    color: ${({theme}) => theme['gray-700']};
    font-weight: 400;
    font-size: 13px;
    padding: 0 31%;
    font-family: ${({theme}) => theme['font-regular']};
    border: 1px solid ${({theme}) => theme['gray-400']};
    margin: 0 10px;
    border-radius: 2px;

    i{
        color: ${({theme}) => theme['gray-500']};
        font-weight: 400;
        font-size: 40px;
        margin-bottom: 16px;
        display: block;
    }

    span:last-child{
        margin-top: 16px;
        display: block;
    }
`